import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { X, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import tenantService from "../../services/tenant.service";

interface DNSRecord {
  id: string;
  name: string;
  type: string;
  content: string;
  proxied: boolean;
  created_on: string;
}

interface DNSRecordFormData {
  type: "CNAME" | "A" | "TXT";
  name: string;
  content: string;
  ttl: number;
  proxied: boolean;
}

interface DNSRecordFormModalProps {
  isOpen: boolean;
  editingRecord?: DNSRecord | null;
  onClose: () => void;
  onSuccess: (data: DNSRecordFormData) => void;
}

const TTL_OPTIONS = [
  { value: 1, label: "Auto" },
  { value: 120, label: "2 minutes" },
  { value: 300, label: "5 minutes" },
  { value: 3600, label: "1 hour" },
];

const DNSRecordFormModal: React.FC<DNSRecordFormModalProps> = ({
  isOpen,
  editingRecord,
  onClose,
  onSuccess,
}) => {
  const isEditing = !!editingRecord;
  const [submitting, setSubmitting] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<DNSRecordFormData>({
    defaultValues: { type: "CNAME", name: "", content: "", ttl: 1, proxied: true },
  });

  const proxied = watch("proxied");

  useEffect(() => {
    if (isOpen) {
      reset(
        editingRecord
          ? {
              type: editingRecord.type as "CNAME" | "A" | "TXT",
              name: editingRecord.name,
              content: editingRecord.content,
              ttl: 1,
              proxied: editingRecord.proxied,
            }
          : { type: "CNAME", name: "", content: "", ttl: 1, proxied: true },
      );
    }
  }, [isOpen, editingRecord, reset]);

  if (!isOpen) return null;

  const onSubmit = async (data: DNSRecordFormData) => {
    setSubmitting(true);
    try {
      const payload = { type: data.type, name: data.name, content: data.content, ttl: data.ttl, proxied: data.proxied };
      if (editingRecord) {
        await tenantService.updateDNSRecord(editingRecord.id, payload);
      } else {
        await tenantService.createDNSRecord(payload);
      }
      onSuccess(data);
    } catch (err: any) {
      toast.error(err?.message || "Failed to save DNS record");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-bold text-slate-900">
            {isEditing ? "Edit DNS Record" : "Add DNS Record"}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          {/* Record Type */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Record Type <span className="text-red-500">*</span>
            </label>
            <select
              {...register("type", { required: true })}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:ring-1 focus:ring-blue-100 focus:border-blue-300 outline-none"
            >
              <option value="CNAME">CNAME</option>
              <option value="A">A</option>
              <option value="TXT">TXT</option>
            </select>
          </div>

          {/* Hostname */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Hostname <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register("name", {
                required: "Hostname is required",
                pattern: { value: /^\S+$/, message: "No spaces allowed" },
              })}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm font-mono text-slate-900 focus:ring-1 focus:ring-blue-100 focus:border-blue-300 outline-none"
              placeholder="app.acme.dociq.com"
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
            )}
            <p className="mt-1 text-xs text-slate-400">
              The full DNS name, e.g. app.tenant.dociq.com
            </p>
          </div>

          {/* Target */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Target (Content) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register("content", { required: "Target is required" })}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm font-mono text-slate-900 focus:ring-1 focus:ring-blue-100 focus:border-blue-300 outline-none"
              placeholder="lb.dociq.com"
            />
            {errors.content && (
              <p className="mt-1 text-xs text-red-500">{errors.content.message}</p>
            )}
            <p className="mt-1 text-xs text-slate-400">Where this record points to</p>
          </div>

          {/* TTL */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">TTL</label>
            <select
              {...register("ttl", { valueAsNumber: true })}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:ring-1 focus:ring-blue-100 focus:border-blue-300 outline-none"
            >
              {TTL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Proxied toggle */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              CloudFlare Proxy
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setValue("proxied", !proxied)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  proxied ? "bg-blue-600" : "bg-slate-200"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    proxied ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
              <span className="text-sm text-slate-600">
                {proxied
                  ? "ON — Traffic proxied through CloudFlare"
                  : "OFF — Direct DNS resolution"}
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Record →"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DNSRecordFormModal;
